import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Eye, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0"
            data-testid="logo"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <div className="flex gap-6 items-center">
            <button onClick={() => navigate('/marketplace')} className="text-white/90 hover:text-white transition-colors">
              Browse
            </button>
            <button onClick={() => navigate('/')} className="text-white/90 hover:text-white transition-colors">
              Home
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 gradient-header">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 text-white" data-testid="about-title">
              About Us
            </h1>
            <p className="text-xl text-white/90">
              Making high-quality, expert-led learning accessible to everyone
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Opening Statement */}
            <div className="text-lg leading-relaxed space-y-6 text-gray-700">
              <p className="text-2xl font-semibold text-gray-900">
                Learning today is everywhere — but finding the right learning isn't easy.
              </p>
              
              <p>
                We're building a marketplace that cuts through the noise and connects people with masterclasses that actually matter. Sessions led by practitioners, not just instructors. Learning that is practical, focused, and immediately useful.
              </p>
              
              <p>
                Our platform brings together experts across domains to host curated masterclasses for learners who value depth over volume. No endless browsing. No generic content. Just high-quality sessions designed to deliver real outcomes.
              </p>
              
              <p>
                For learners, it's a place to discover meaningful, experience-driven learning.<br/>
                For trainers, it's a platform to share their expertise with the right audience and grow their reach.
              </p>
              
              <p>
                We're starting with a simple belief — the best learning comes directly from people who've done the work.
              </p>
              
              <p className="font-semibold text-gray-900">
                And we're building the platform to make that accessible to everyone.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission, Vision, What We're Building */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-8 border border-gray-200"
            >
              <div className="w-12 h-12 rounded-full bg-[#00a3af]/10 flex items-center justify-center mb-6">
                <Target size={24} className="text-[#00a3af]" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Mission</h2>
              <p className="text-gray-700 leading-relaxed">
                To make high-quality, expert-led learning simple, accessible, and outcome-driven.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-8 border border-gray-200"
            >
              <div className="w-12 h-12 rounded-full bg-[#34c759]/10 flex items-center justify-center mb-6">
                <Eye size={24} className="text-[#34c759]" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Vision</h2>
              <p className="text-gray-700 leading-relaxed">
                To become the most trusted platform for discovering and attending impactful masterclasses.
              </p>
            </motion.div>

            {/* What We're Building */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-8 border border-gray-200"
            >
              <div className="w-12 h-12 rounded-full bg-[#004d7a]/10 flex items-center justify-center mb-6">
                <Sparkle size={24} className="text-[#004d7a]" weight="duotone" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">What We're Building</h2>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-[#00a3af] mr-2">•</span>
                  <span>A curated marketplace for expert-led masterclasses</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#00a3af] mr-2">•</span>
                  <span>A seamless discovery and booking experience</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#00a3af] mr-2">•</span>
                  <span>A growing ecosystem of learners and practitioners</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-2xl font-semibold text-gray-900">
              We're early, but intentional.
            </p>
            <p className="text-xl text-gray-700">
              And this is just the beginning.
            </p>
            <div className="pt-8">
              <Button
                onClick={() => navigate('/marketplace')}
                className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-12 py-6 text-lg font-bold"
              >
                Explore Masterclasses
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="gradient-footer text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold mb-4">Company</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Resources</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors">Browse Courses</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Legal</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Get Started</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/auth?role=instructor')} className="hover:text-white transition-colors">Teach a Masterclass</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-white/70 text-sm">© 2026 NeoMaestro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
